/**
 * Formats text containing markdown-style formatting into an array of formatted segments
 * @param {string} text - The text to format
 * @returns {Array} Array of objects with type and content properties
 */
export function formatMarkdownText(text) {
  if (!text || typeof text !== 'string') {
    return [{ type: 'text', content: text || '' }];
  }

  // 🔥 CRITICAL FIX: Strip markdown images ![alt](url) BEFORE processing
  // This prevents duplicate images (markdown + plain URL)
  // Pattern matches: ![anything](url) or ![](url)
  text = text.replace(/!\[.*?\]\([^)]+\)/g, '');

  // Also remove leftover empty brackets or exclamation marks
  text = text.replace(/^\s*!\s*$/gm, '');
  text = text.replace(/^\s*\[\s*\]\s*$/gm, '');

  // First, replace lines that start with "- " (dash followed by space) with bullet points
  const lines = text.split('\n');
  const processedLines = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trimStart();
    
    // Check if line starts with "- " (after trimming leading whitespace)
    if (trimmedLine.startsWith('- ')) {
      const leadingWhitespace = line.substring(0, line.indexOf('- '));
      // Replace "- " with "• "
      const replacedLine = leadingWhitespace + '• ' + trimmedLine.substring(2);
      processedLines.push(replacedLine);
    } else {
      processedLines.push(line);
    }
  }

  // Join the lines back together
  const processedText = processedLines.join('\n');

  // Now process the text for ** and * formatting
  const segments = [];
  let lastIndex = 0;

  // Updated regex to handle both ** and * patterns
  const regex = /\*\*([^*]*)\*\*|\*([^*]+)\*/g;
  let match;

  while ((match = regex.exec(processedText)) !== null) {
    // Add any text before the match
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: processedText.substring(lastIndex, match.index)
      });
    }

    // Determine if it's bold (**) or larger (*)
    if (match[0].startsWith('**')) {
      // Bold text (double asterisks)
      segments.push({
        type: 'bold',
        content: match[1]
      });
    } else {
      // Larger text (single asterisk)
      segments.push({
        type: 'larger',
        content: match[2]
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text after the last match
  if (lastIndex < processedText.length) {
    segments.push({
      type: 'text',
      content: processedText.substring(lastIndex)
    });
  }

  // If no segments were created, return the entire text as a single segment
  if (segments.length === 0) {
    return [{ type: 'text', content: processedText }];
  }

  return segments;
}